import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const AddSuperAdmin = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { addAdmin, adminID } = location.state || "";
  const statuses = ["ACTIVE", "INACTIVE"];

  // ===== validation ========
  const schema = Yup.object().shape({
    firstName: Yup.string().required("Please enter the first name"),
    lastName: Yup.string().required("Please enter the last name"),
    contactNumber: Yup.string().required("Please enter the contact number"),
    email: Yup.string()
      .required("Please enter your Email Address")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid Email Address"
      ),
    address: Yup.string().required("Please enter the address"),
    city: Yup.string().required("Please enter the city"),
    state: Yup.string().required("Please enter the state"),
    pincode: Yup.string()
      .matches(/^[1-9][0-9]{5}$/, "Please enter a valid 6-digit pincode")
      .required("Please enter the pincode"),
    gender: Yup.string().required("Please select the gender"),
    dob: Yup.date()
      .required("Please enter your date of birth")
      .max(new Date(), "DOB cannot be in the future")
      .typeError("Please enter a valid date"),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      contactNumber: "",
      email: "",
      status: "ACTIVE",
      address: "",
      city: "",
      state: "",
      pincode: "",
      gender: "Male",
      dob: "",
    },
  });

  // ===== API calls =======
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: data?.email,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        last_name: data?.lastName,
        first_name: data?.firstName,
        gender: data?.gender,
        phone: data?.contactNumber,
        city: data?.city,
        state: data?.state,
        address: data?.address,
        pincode: data?.pincode,
        status: "ACTIVE",
      };

      const updatePayload = {
        user_id: adminID,
        email: data?.email,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        last_name: data?.lastName,
        first_name: data?.firstName,
        gender: data?.gender,
        phone: data?.contactNumber,
        city: data?.city,
        state: data?.state,
        address: data?.address,
        pincode: data?.pincode,
        status: data?.status,
      };

      if (addAdmin !== "true" && addAdmin !== true) {
        const res = await api.post("UpdateSuperAdminByID", updatePayload);
        if (res?.data?.status === "success") {
          navigate("/superAdminList");
          showSnackbar("success", res?.data?.message);
        } else {
          showSnackbar("error", res?.data?.message);
        }
      } else {
        const res = await api.post("AddSuperAdmin", payload);
        if (res?.data?.status === "success") {
          navigate("/superAdminList");
          showSnackbar("success", res?.data?.message);
        } else {
          showSnackbar("error", res?.data?.message);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewSuperAdminByID", { user_id: adminID });
      if (res?.data?.status === "success") {
        reset({
          firstName: res.data.data.first_name,
          lastName: res.data.data.last_name,
          contactNumber: res.data.data.phone,
          email: res.data.data.email,
          status: res.data.data.status,
          address: res.data.data.address,
          city: res.data.data.city,
          state: res.data.data.state,
          pincode: res.data.data.pincode,
          gender: res.data.data.gender,
          dob: res.data.data.dob,
        });
      }
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching super admin by ID:", error);
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    if (adminID !== null) {
      fetchById();
    }
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Saving super admin..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              marginBottom: "20px",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* Form Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  border: "1px",
                  borderRadius: "10px",
                }}
              >
                <AdminPanelSettingsIcon
                  sx={{ color: "white", fontSize: "35px" }}
                />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {addAdmin === "true" || addAdmin === true ? "Add" : "Edit"}{" "}
                  Super Admin
                </Typography>
              </Grid>
            </Grid>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  p: 3,
                }}
              >
                {/* Basic Info */}
                <Grid
                  sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
                >
                  <AdminPanelSettingsIcon
                    sx={{ color: "#1c97e3", fontSize: "30px" }}
                  />
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid sx={{ display: "flex", gap: 3 }}>
                  {/* First Name */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      First Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="First Name"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Last Name"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex", gap: 3 }}>
                  {/* Gender */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Gender <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  {/* DOB */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Date of Birth <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="dob"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD-MM-YYYY"
                            value={
                              field.value
                                ? dayjs.isDayjs(field.value)
                                  ? field.value
                                  : dayjs(field.value)
                                : null
                            }
                            // ✅ Keep as dayjs during typing to prevent refresh
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dob,
                                helperText: errors.dob?.message,
                                placeholder: "DD-MM-YYYY",
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "white" },
                                    "&:hover fieldset": { borderColor: "#d1d1d1" },
                                    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                  },
                                  "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "white" },
                                  "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#d1d1d1" },
                                  "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#1c97e3" },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Status */}
                {addAdmin !== "true" && (
                  <Grid sx={{ width: "49%" }}>
                    <Typography>
                      Status <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.status}
                          helperText={errors.status?.message}
                        >
                          {statuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}

                {/* Contact Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <MailOutlineIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Contact Number <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="contactNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="tel"
                          {...field}
                          fullWidth
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, "");
                            field.onChange(e);
                          }}
                          placeholder="Contact Number"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.contactNumber}
                          helperText={errors.contactNumber?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Email"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.email}
                          helperText={errors.email?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Address Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <LocationOnIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Address Information
                  </Typography>
                </Grid>
                <Typography>
                  Address <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Address"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "white",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d1d1d1",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#1c97e3",
                          },
                        },
                      }}
                    />
                  )}
                />

                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      City <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="City"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.city}
                          helperText={errors.city?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      State <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="State"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.state}
                          helperText={errors.state?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Pincode <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Pincode"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                          error={!!errors.pincode}
                          helperText={errors.pincode?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {addAdmin === "true" || addAdmin === true
                      ? "Add"
                      : "Update"}{" "}
                    Admin
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddSuperAdmin;
